import {
  InputStartAdornment,
  SelectV4,
  TextInputV4,
  Truncate,
} from '@labrav/react-components'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import {
  Divider,
  Grid,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { FormikProps, useFormikContext } from 'formik'
import { cloneDeep, get, isEmpty } from 'lodash'
import { useCallback } from 'react'
import { getValidValueBetweenMinMax, isNonEmptyNumber } from '@labrav/common'
import {
  AdditionalMeter,
  AzureOfferPlan,
  AzurePricingType,
  RecurrentPriceModeType,
} from '../../../../modules/privateOffer/types'
import { Product } from '../../../../modules/productsListing/types'
import { MinMax } from '../Plan/PlanRow/MinMax/MinMax'
import { DisplayValueData } from '../Plan/PlanRow/MeteringDimensions/MeteringDimensions'
import { useStyles as useMeteringStyles } from '../Plan/PlanRow/MeteringDimensions/MeteringDimensions.styles'
import { useStyles } from '../Plan/PlanRow/PlanRow.styles'
import {
  convertContainerProductPlanToOfferPlan,
  getContainerCustomMeters,
  getContainerPlanTcvValue,
  getContainerSystemMeterPriceTitle,
  getContainerTcvFromMeters,
  getMeterDisplayName,
  hasCustomMeteringDimensions,
  shouldShowContainerTcv,
  updateContainerPlanDiscount,
} from '../../../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'

const useContainerPlanStyles = makeStyles()(theme => ({
  fieldWidth248: {
    width: theme.spacing(31),
  },
  pricePerUnitInput: {
    width: theme.spacing(31),
    marginLeft: 'auto',
    '&.MuiFormControl-root': {
      width: theme.spacing(31),
    },
  },
}))

const ContainerPlanTcv = ({
  plan,
  index,
  product,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
}) => {
  const { classes } = useStyles()
  const { classes: containerClasses } = useContainerPlanStyles()
  const { values, touched, errors, handleChange, handleBlur } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const formPlan = (values?.plans as AzureOfferPlan[])?.[index] || plan

  if (!shouldShowContainerTcv(formPlan, product)) {
    return null
  }

  const azurePricingType = values?.azurePricingType as AzurePricingType
  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const tcvTitle = getContainerSystemMeterPriceTitle(formPlan, azurePricingType)

  const tcvKeyName = `plans.${index}.pricing.recurrentPrice.prices.0.pricePerPaymentInUsd`
  const tcvValue: number | undefined =
    (get(values, tcvKeyName) as number | undefined) ??
    getContainerPlanTcvValue(formPlan) ??
    getContainerTcvFromMeters(formPlan?.pricing?.customMeters?.meters)

  return (
    <Grid container width={'100%'} spacing={3}>
      <Grid size={6} width={'248px'}>
        <div className={containerClasses.fieldWidth248}>
          {!isDiscountPricing ? (
            <TextInputV4
              keyName={tcvKeyName}
              title={tcvTitle}
              type="number"
              placeHolder=""
              keyToPrevent={['+', '-', 'e']}
              value={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
              updatedValue={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
              startAdornment={<InputStartAdornment text="$" />}
              alignRight
              isError={
                !!(
                  get(touched, tcvKeyName, false) &&
                  get(errors, tcvKeyName, false)
                )
              }
              onChange={handleChange}
              isReadOnly={false}
              isRequired={false}
              errorText={
                (!!get(touched, tcvKeyName, false) &&
                  get(errors, tcvKeyName, '')) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          ) : (
            <div className={classes.discountedTcv}>
              <div className={classes.title}>{tcvTitle}</div>
              <DisplayValueData id={tcvKeyName} value={tcvValue} isPrice />
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

const ContainerCustomMeteringDimensions = ({
  plan,
  index,
}: {
  plan: AzureOfferPlan
  index: number
}) => {
  const { classes } = useMeteringStyles()
  const { classes: containerClasses } = useContainerPlanStyles()
  const { values, setFieldValue, errors, handleBlur } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const azurePricingType = values?.azurePricingType
  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const meteringList = getContainerCustomMeters(plan)

  if (Object.keys(meteringList).length === 0) {
    return null
  }

  const meteringColumn = [
    'Name',
    'Unit',
    `${isDiscountPricing ? 'Discounted price' : 'Price'} per unit`,
  ]

  const updatePlan = (meteringKey: string, updatedMeter: AdditionalMeter) => {
    if (plan?.pricing?.customMeters) {
      const customMetersData = {
        ...plan?.pricing?.customMeters,
        meters: {
          ...plan?.pricing?.customMeters.meters,
          [meteringKey]: updatedMeter,
        },
      }
      const updateplan = {
        ...plan,
        pricing: {
          ...plan.pricing,
          customMeters: {
            ...customMetersData,
          },
        },
      }
      const planList = cloneDeep(values.plans) as AzureOfferPlan[]
      planList[index] = {
        ...updateplan,
      }
      setFieldValue('plans', planList, true)
    }
  }

  const tableDataCellClass = clsx(classes.rowText, classes.tableCell)

  return (
    <div>
      <div className={classes.headingWrapper}>
        <div className={classes.plansHeading}>Metering dimensions</div>
      </div>
      <TableContainer>
        <Table aria-label="container custom metering dimensions table">
          <TableHead className={classes.tableHader}>
            <TableRow>
              {meteringColumn.map((col, colIndex) => (
                <TableCell
                  key={`container-custom-metering-col-${colIndex}-${col}`}
                  className={clsx(
                    classes.headerTitle,
                    classes.tableCell,
                    colIndex > 1 ? classes.textRight : ''
                  )}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(meteringList).map(meteringKey => {
              const meter = { ...meteringList[meteringKey] }
              const meterErrorObject = get(
                errors?.plans,
                `[${index}].pricing.customMeters.meters.[${meteringKey}]`,
                {}
              )

              const updateMeter =
                () => (event: React.ChangeEvent<HTMLInputElement>) => {
                  const updatedMeter = {
                    ...meter,
                    pricePerPaymentInUsd:
                      event.target.value !== ''
                        ? Number(event.target.value)
                        : event.target.value,
                  }
                  updatePlan(meteringKey, updatedMeter as AdditionalMeter)
                }

              return (
                <TableRow className={classes.row} key={meteringKey}>
                  <TableCell className={tableDataCellClass} width={'150px'}>
                    <div>{getMeterDisplayName(meter, meteringKey)}</div>
                  </TableCell>
                  <TableCell className={tableDataCellClass} width={'120px'}>
                    <div>{meter.unitOfMeasure}</div>
                  </TableCell>
                  <TableCell
                    className={clsx(tableDataCellClass, classes.textRight)}
                    width={'248px'}
                  >
                    {isDiscountPricing ? (
                      <div className={containerClasses.pricePerUnitInput}>
                        <DisplayValueData
                          isPrice={true}
                          id="pricePerPaymentInUsd"
                          value={meter.pricePerPaymentInUsd}
                        />
                      </div>
                    ) : (
                      <div className={containerClasses.pricePerUnitInput}>
                        <TextInputV4
                          type="number"
                          title=""
                          placeHolder=""
                          isReadOnly={false}
                          errorText={
                            get(
                              meterErrorObject,
                              'pricePerPaymentInUsd',
                              ''
                            ) as string
                          }
                          isError={
                            !isEmpty(
                              get(
                                meterErrorObject,
                                'pricePerPaymentInUsd',
                                ''
                              ) as string
                            )
                          }
                          startAdornment={<InputStartAdornment text="$" />}
                          alignRight
                          isRequired={true}
                          value={
                            isNonEmptyNumber(meter.pricePerPaymentInUsd)
                              ? meter.pricePerPaymentInUsd!
                              : ''
                          }
                          updatedValue={
                            isNonEmptyNumber(meter.pricePerPaymentInUsd)
                              ? meter.pricePerPaymentInUsd!
                              : ''
                          }
                          onChange={updateMeter()}
                          onBlur={handleBlur}
                          keyName={`plans.${index}.pricing.customMeters.meters.${meteringKey}.pricePerPaymentInUsd`}
                          variant="regularV2"
                          keyToPrevent={['+', '-', 'e']}
                          showSpinner={false}
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

const ContainerPlanPricing = ({
  index,
  plan,
  product,
}: {
  index: number
  plan: AzureOfferPlan
  product: Product
}) => {
  const { classes } = useStyles()

  const shouldShowUserMinMax =
    plan?.pricing?.recurrentPrice?.recurrentPriceMode ===
    RecurrentPriceModeType.PERUSER

  const shouldShowCustomMetering = hasCustomMeteringDimensions(plan)
  const showContainerTcv = shouldShowContainerTcv(plan, product)

  const shouldShowDivider = shouldShowUserMinMax || shouldShowCustomMetering

  return (
    <>
      {showContainerTcv && (
        <ContainerPlanTcv plan={plan} index={index} product={product} />
      )}
      {shouldShowDivider && <Divider className={classes.divider} />}
      {shouldShowUserMinMax && <MinMax plan={plan} index={index} />}
      {shouldShowCustomMetering && (
        <ContainerCustomMeteringDimensions plan={plan} index={index} />
      )}
    </>
  )
}

export const ContainerPlanRow = ({
  plan,
  index,
  product,
  planIds,
  remove,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
  planIds: string[]
  remove?: <X = unknown>(index: number) => X | undefined
}) => {
  const { classes } = useStyles()
  const { values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const plans = (values?.plans || []) as AzureOfferPlan[]

  const azurePricingType = values?.azurePricingType
  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const productPlans = product?.plans
    ?.filter(
      planItem =>
        planItem?.planId === plan?.labraPlanId ||
        !planIds.includes(planItem?.planId)
    )
    ?.filter(
      planItem =>
        !(
          planItem?.isPrivate ||
          planItem?.isStopSell ||
          planItem?.hasFreeTrials
        )
    )
    .map(planItem => ({
      label: planItem?.displayName,
      value: planItem?.planId,
    }))

  const handleChangePlanSelection = (e: SelectChangeEvent) => {
    const selectedPlan = product?.plans?.find(
      item => item?.planId === e.target.value
    )
    if (selectedPlan) {
      const offerPlan = convertContainerProductPlanToOfferPlan(selectedPlan)

      setFieldValue(`plans.${[index]}`, {
        ...offerPlan,
        discountPercentage: null,
      })
    }
  }

  const updateContainerDiscount = useCallback(
    ({ discountPercentage }: { discountPercentage?: number }) => {
      const currentPlan =
        ((values.plans as AzureOfferPlan[])?.[index] as AzureOfferPlan) || plan
      const updatedPlan = updateContainerPlanDiscount({
        plan: currentPlan,
        product,
        discountPercentage,
      })

      if (!updatedPlan) {
        return
      }

      const updatedPlans = [...(values.plans as AzureOfferPlan[])]
      updatedPlans[index] = cloneDeep(updatedPlan) as AzureOfferPlan
      setFieldValue('plans', [...cloneDeep(updatedPlans)])
    },
    [values, setFieldValue, index, plan, product]
  )

  const handleDiscountUpdate = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = getValidValueBetweenMinMax(
      event.target.value as string,
      1,
      100
    )
    await updateContainerDiscount({
      discountPercentage: value || undefined,
    })
  }

  return (
    <div className={classes.planRowWrapper}>
      <div className={classes.header}>
        <div className={classes.plansHeading}>Plans</div>
        <div>
          {plans.length > 1 && (
            <FontAwesomeIcon
              icon={faTrash}
              style={{
                width: '14px',
                height: '20px',
                color: '#6A777A',
                cursor: 'pointer',
              }}
              data-testid="trash-metaData"
              onClick={() => remove && remove(index)}
            />
          )}
        </div>
      </div>
      <Grid container width={'100%'} spacing={3}>
        <Grid size={4}>
          <div className={classes.planSelect}>
            <SelectV4
              field={{
                title: 'Select plan',
                placeHolder: 'Select plan',
                humanReadableKey: 'plan',
                id: `plans.${[index]}.labraPlanId`,
                isRequired: false,
                isReadOnly: false,
                isCustom: false,
                defaultValue: '',
                dataType: 'enum' as DataType,
                options: productPlans,
              }}
              size="small"
              value={plan?.labraPlanId || ''}
              onChange={handleChangePlanSelection}
              onBlur={handleBlur}
              isError={
                !!(
                  get(touched, `plans.${[index]}.labraPlanId`, false) &&
                  get(errors, `plans.${[index]}.labraPlanId`, false)
                )
              }
              errorText={
                (!!get(touched, `plans.${[index]}.labraPlanId`, false) &&
                  get(errors, `plans.${[index]}.labraPlanId`, '')) ||
                ''
              }
            />
          </div>
        </Grid>
        {plan?.labraPlanId && (
          <>
            <Grid size={isDiscountPricing ? 5 : 6}>
              <div className={classes.title}>Description</div>
              <div className={classes.content}>
                <Truncate showIcon={false} clipboardTooltip>
                  {plan.description}
                </Truncate>
              </div>
            </Grid>
            {isDiscountPricing && (
              <Grid size={3}>
                <div className={classes.content}>
                  <TextInputV4
                    keyName={`plans.${[index]}.discountPercentage`}
                    title="Discount (%)"
                    type="number"
                    placeHolder=""
                    alignRight
                    minNumber={1}
                    maxNumber={100}
                    value={plan?.discountPercentage || ''}
                    updatedValue={plan?.discountPercentage || ''}
                    isError={
                      !!(
                        get(
                          touched,
                          `plans.${[index]}.discountPercentage`,
                          false
                        ) &&
                        get(
                          errors,
                          `plans.${[index]}.discountPercentage`,
                          false
                        )
                      )
                    }
                    onChange={handleDiscountUpdate}
                    isReadOnly={false}
                    isRequired={false}
                    errorText={
                      (!!get(
                        touched,
                        `plans.${[index]}.discountPercentage`,
                        false
                      ) &&
                        get(
                          errors,
                          `plans.${[index]}.discountPercentage`,
                          ''
                        )) ||
                      ''
                    }
                    showSpinner={false}
                    onBlur={handleBlur}
                  />
                </div>
              </Grid>
            )}
          </>
        )}
      </Grid>
      {plan?.labraPlanId && (
        <ContainerPlanPricing index={index} plan={plan} product={product} />
      )}
    </div>
  )
}
